import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Mail,
  ShieldCheck,
  CheckCircle2,
  ArrowLeft,
  RefreshCw,
  ShieldAlert,
  Clock,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ErrorAlert } from "@/components/ui/ErrorAlert";
import { apiService } from "@/services/api";
import { getLocalizedPublicPath } from "@/lib/publicLocaleRouting";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;
const OTP_TTL_MINUTES = 15;

interface ErrorDetails {
  type: "error" | "warning" | "info";
  icon: React.ElementType;
  title: string;
  message: string;
  suggestion?: string;
}

export const VerifyEmailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const email = (location.state as { email?: string })?.email || "";

  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expiresAt, setExpiresAt] = useState<Date>(
    () => new Date(Date.now() + OTP_TTL_MINUTES * 60 * 1000)
  );
  const [timeLeft, setTimeLeft] = useState(OTP_TTL_MINUTES * 60);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      navigate(
        getLocalizedPublicPath(
          location.pathname,
          "/login",
          i18n.resolvedLanguage || i18n.language
        ),
        { replace: true }
      );
    }
  }, [email, navigate, location.pathname, i18n.resolvedLanguage, i18n.language]);

  // Countdown for code expiration
  useEffect(() => {
    const interval = setInterval(() => {
      const diff = Math.max(
        0,
        Math.floor((expiresAt.getTime() - Date.now()) / 1000)
      );
      setTimeLeft(diff);
      if (diff === 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  // Resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(
      () => setResendCooldown((c) => c - 1),
      1000
    );
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Auto-focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const clearOtp = useCallback(() => {
    setOtp(Array(OTP_LENGTH).fill(""));
    inputRefs.current[0]?.focus();
  }, []);

  const handleSubmit = useCallback(
    async (code: string) => {
      if (code.length !== OTP_LENGTH || isLoading) return;

      setErrorDetails(null);
      setIsLoading(true);

      try {
        const response = await apiService.verifyEmailOtp({
          email,
          otp: code,
        });

        if (response.data?.verified) {
          setIsVerified(true);
          setTimeout(() => {
            navigate(
              getLocalizedPublicPath(
                location.pathname,
                "/login",
                i18n.resolvedLanguage || i18n.language
              ),
              {
                state: {
                  successMessage: t("auth.verifyEmail.success.message"),
                },
              }
            );
          }, 2000);
        }
      } catch (err: unknown) {
        let errorMsg = "";
        if (err instanceof Error) {
          errorMsg = err.message.toLowerCase();
        } else if (
          err !== null &&
          typeof err === "object" &&
          "message" in err
        ) {
          errorMsg = String(
            (err as Record<string, unknown>).message
          ).toLowerCase();
        }

        let errorInfo: ErrorDetails;

        if (
          errorMsg.includes("401") ||
          errorMsg.includes("invalid") ||
          errorMsg.includes("inválid")
        ) {
          errorInfo = {
            type: "error",
            icon: ShieldAlert,
            title: t("auth.verifyEmail.errors.invalidCode"),
            message: t("auth.verifyEmail.errors.invalidCodeMsg"),
            suggestion: t("auth.verifyEmail.errors.invalidCodeSuggestion"),
          };
        } else if (
          errorMsg.includes("expired") ||
          errorMsg.includes("expirado") ||
          errorMsg.includes("expirou")
        ) {
          errorInfo = {
            type: "warning",
            icon: Clock,
            title: t("auth.verifyEmail.errors.expiredCode"),
            message: t("auth.verifyEmail.errors.expiredCodeMsg"),
            suggestion: t("auth.verifyEmail.errors.expiredCodeSuggestion"),
          };
        } else if (
          errorMsg.includes("not found") ||
          errorMsg.includes("não encontrad")
        ) {
          errorInfo = {
            type: "warning",
            icon: AlertTriangle,
            title: t("auth.verifyEmail.errors.notFound"),
            message: t("auth.verifyEmail.errors.notFoundMsg"),
            suggestion: t("auth.verifyEmail.errors.notFoundSuggestion"),
          };
        } else if (
          errorMsg.includes("already verified") ||
          errorMsg.includes("já verificad")
        ) {
          errorInfo = {
            type: "info",
            icon: CheckCircle2,
            title: t("auth.verifyEmail.errors.alreadyVerified"),
            message: t("auth.verifyEmail.errors.alreadyVerifiedMsg"),
          };
        } else {
          errorInfo = {
            type: "error",
            icon: XCircle,
            title: t("auth.verifyEmail.errors.generic"),
            message: t("auth.verifyEmail.errors.genericMsg"),
            suggestion: t("auth.verifyEmail.errors.genericSuggestion"),
          };
        }

        setErrorDetails(errorInfo);
        clearOtp();
      } finally {
        setIsLoading(false);
      }
    },
    [email, isLoading, navigate, location.pathname, i18n, t, clearOtp]
  );

  const handleChange = useCallback(
    (index: number, value: string) => {
      // Allow only digits
      const digit = value.replace(/\D/g, "").slice(-1);

      setOtp((prev) => {
        const next = [...prev];
        next[index] = digit;
        return next;
      });

      if (errorDetails) setErrorDetails(null);

      if (digit && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      // Auto-submit when last digit entered
      if (digit && index === OTP_LENGTH - 1) {
        setOtp((prev) => {
          const next = [...prev];
          next[index] = digit;
          const code = next.join("");
          if (code.length === OTP_LENGTH) {
            setTimeout(() => handleSubmit(code), 50);
          }
          return next;
        });
      }
    },
    [errorDetails, handleSubmit]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace") {
        if (!otp[index] && index > 0) {
          inputRefs.current[index - 1]?.focus();
          setOtp((prev) => {
            const next = [...prev];
            next[index - 1] = "";
            return next;
          });
        } else {
          setOtp((prev) => {
            const next = [...prev];
            next[index] = "";
            return next;
          });
        }
        e.preventDefault();
      } else if (e.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [otp]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, OTP_LENGTH);

      if (!pasted) return;

      const newOtp = Array(OTP_LENGTH).fill("");
      for (let i = 0; i < pasted.length; i++) {
        newOtp[i] = pasted[i];
      }
      setOtp(newOtp);

      if (errorDetails) setErrorDetails(null);

      // Focus the next empty input or last
      const nextEmpty = pasted.length < OTP_LENGTH ? pasted.length : OTP_LENGTH - 1;
      inputRefs.current[nextEmpty]?.focus();

      // Auto-submit if full code pasted
      if (pasted.length === OTP_LENGTH) {
        setTimeout(() => handleSubmit(pasted), 50);
      }
    },
    [errorDetails, handleSubmit]
  );

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) return;

    setIsResending(true);
    setErrorDetails(null);

    try {
      const response = await apiService.resendEmailOtp({ email });

      if (response.data?.expiresAt) {
        setExpiresAt(new Date(response.data.expiresAt));
      } else {
        setExpiresAt(new Date(Date.now() + OTP_TTL_MINUTES * 60 * 1000));
      }

      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      clearOtp();
    } catch (err: unknown) {
      let errorMsg = "";
      if (err instanceof Error) errorMsg = err.message.toLowerCase();

      if (
        errorMsg.includes("already verified") ||
        errorMsg.includes("já verificad")
      ) {
        setErrorDetails({
          type: "info",
          icon: CheckCircle2,
          title: t("auth.verifyEmail.errors.alreadyVerified"),
          message: t("auth.verifyEmail.errors.alreadyVerifiedMsg"),
        });
      } else {
        setErrorDetails({
          type: "error",
          icon: XCircle,
          title: t("auth.verifyEmail.errors.resendFailed"),
          message: t("auth.verifyEmail.errors.resendFailedMsg"),
          suggestion: t("auth.verifyEmail.errors.resendFailedSuggestion"),
        });
      }
    } finally {
      setIsResending(false);
    }
  };

  const formatTimeLeft = () => {
    const mins = Math.floor(timeLeft / 60);
    const secs = timeLeft % 60;
    if (mins > 0) {
      return `${t("auth.verifyEmail.minutes", { minutes: mins })} ${secs > 0 ? t("auth.verifyEmail.seconds", { seconds: secs }) : ""}`.trim();
    }
    return t("auth.verifyEmail.seconds", { seconds: secs });
  };

  const isCodeComplete = otp.every((d) => d !== "");

  if (!email) return null;

  return (
    <div className="min-h-screen flex">
      {/* LEFT BRAND PANEL */}
      <div
        className={cn(
          "hidden lg:flex lg:w-1/2 xl:w-[58%]",
          "relative flex-col justify-between p-12 overflow-hidden",
          "bg-gradient-to-br from-primary-900 via-primary-700 to-primary-600",
          "animate-in fade-in slide-in-from-left-8 duration-700"
        )}
      >
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage:
              'url("https://images.unsplash.com/photo-1563013544-824ae1b704d3?fm=jpg&q=60&w=3000&auto=format&fit=crop")',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-primary/75 pointer-events-none"
        />

        {/* Decorative glow orbs */}
        <div
          aria-hidden
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl pointer-events-none"
          style={{ backgroundColor: "hsl(var(--secondary) / 0.14)" }}
        />
        <div
          aria-hidden
          className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full blur-3xl pointer-events-none"
          style={{ backgroundColor: "hsl(var(--accent) / 0.07)" }}
        />
        {/* Grid pattern */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(hsl(var(--primary-foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary-foreground)) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        {/* Logo */}
        <div className="relative animate-in fade-in zoom-in-95 duration-1000">
          <img
            src="/logo.svg"
            alt="ambcar"
            className="h-9 w-auto brightness-0 invert"
          />
        </div>

        {/* Central content */}
        <div className="relative space-y-10 animate-in fade-in slide-in-from-bottom-6 duration-700 delay-200">
          <div className="space-y-5">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-secondary">
              {t("auth.verifyEmail.brand.headline")}
            </p>

            <div className="space-y-4">
              {/* Shield icon */}
              <div className="w-16 h-16 rounded-2xl bg-secondary/20 flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-secondary" />
              </div>
              <p className="text-2xl xl:text-[1.75rem] font-bold text-primary-foreground leading-snug">
                {t("auth.verifyEmail.brand.text1")}
              </p>
              <p className="text-sm text-primary-200 leading-relaxed max-w-md">
                {t("auth.verifyEmail.brand.text2")}
              </p>
            </div>
          </div>

          {/* Feature list */}
          <ul className="space-y-4">
            {(
              [
                "auth.verifyEmail.brand.feature1",
                "auth.verifyEmail.brand.feature2",
                "auth.verifyEmail.brand.feature3",
              ] as const
            ).map((key) => (
              <li key={key} className="flex items-center gap-3">
                <CheckCircle2
                  className="h-5 w-5 flex-shrink-0 text-secondary"
                  strokeWidth={1.5}
                />
                <span className="text-sm font-medium text-primary-100">
                  {t(key)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom copyright */}
        <div className="relative animate-in fade-in duration-700 delay-500">
          <p className="text-xs text-primary-300">
            &copy; {new Date().getFullYear()} ambcar &middot;{" "}
            {t("auth.login.subtitle")}
          </p>
        </div>
      </div>

      {/* RIGHT FORM PANEL */}
      <div
        className={cn(
          "w-full lg:w-1/2 xl:w-[42%]",
          "flex items-center justify-center",
          "bg-background",
          "px-6 py-12 lg:px-16",
          "animate-in fade-in slide-in-from-right-8 duration-700"
        )}
      >
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center animate-in fade-in zoom-in-95 duration-500">
            <img src="/logo.svg" alt="ambcar" className="h-8 w-auto" />
          </div>

          {/* Header */}
          <div className="text-center space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center">
              <Mail className="w-7 h-7 text-accent" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              {t("auth.verifyEmail.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("auth.verifyEmail.subtitle")}
            </p>
            <p className="text-sm font-semibold text-foreground">{email}</p>
          </div>

          <div className="space-y-5">
            {/* Success state */}
            {isVerified && (
              <ErrorAlert
                type="success"
                title={t("auth.verifyEmail.success.title")}
                message={t("auth.verifyEmail.success.message")}
              />
            )}

            {/* Error state */}
            {errorDetails && !isVerified && (
              <ErrorAlert
                type={errorDetails.type}
                title={errorDetails.title}
                message={errorDetails.message}
                suggestion={errorDetails.suggestion}
                icon={errorDetails.icon}
                onClose={() => setErrorDetails(null)}
              />
            )}

            {!isVerified && (
              <>
                {/* OTP Input */}
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
                  <label className="block text-sm font-medium text-foreground text-center">
                    {t("auth.verifyEmail.instruction")}
                  </label>

                  <div
                    className="flex justify-center gap-2 sm:gap-3"
                    onPaste={handlePaste}
                  >
                    {otp.map((digit, index) => (
                      <React.Fragment key={index}>
                        {index === 3 && (
                          <div className="flex items-center px-1">
                            <span className="text-xl text-muted-foreground/40 font-light">
                              &ndash;
                            </span>
                          </div>
                        )}
                        <input
                          ref={(el) => {
                            inputRefs.current[index] = el;
                          }}
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleChange(index, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          disabled={isLoading}
                          aria-label={`Digit ${index + 1}`}
                          className={cn(
                            "w-11 h-14 sm:w-13 sm:h-16 text-center text-xl sm:text-2xl font-bold",
                            "rounded-xl border-2 bg-background",
                            "transition-all duration-200 ease-out",
                            "outline-none",
                            "focus:border-accent focus:ring-2 focus:ring-accent/30 focus:scale-105",
                            "placeholder:text-muted-foreground/20",
                            "disabled:opacity-50 disabled:cursor-not-allowed",
                            digit
                              ? "border-accent/50 text-foreground"
                              : "border-border text-foreground",
                            errorDetails &&
                              "border-destructive/50 focus:border-destructive focus:ring-destructive/30"
                          )}
                          placeholder={t("auth.verifyEmail.placeholder")}
                        />
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Timer */}
                <div className="text-center animate-in fade-in duration-500 delay-300">
                  {timeLeft > 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {t("auth.verifyEmail.expiresIn")}{" "}
                      <span
                        className={cn(
                          "font-semibold font-mono",
                          timeLeft <= 60
                            ? "text-destructive"
                            : "text-foreground"
                        )}
                      >
                        {formatTimeLeft()}
                      </span>
                    </p>
                  ) : (
                    <p className="text-xs font-semibold text-destructive">
                      {t("auth.verifyEmail.expired")}
                    </p>
                  )}
                </div>

                {/* Submit button */}
                <button
                  onClick={() => handleSubmit(otp.join(""))}
                  disabled={!isCodeComplete || isLoading || timeLeft === 0}
                  className={cn(
                    "w-full h-12 rounded-xl font-semibold text-sm",
                    "transition-all duration-200",
                    "flex items-center justify-center gap-2",
                    isCodeComplete && !isLoading && timeLeft > 0
                      ? "bg-accent text-accent-foreground hover:opacity-90 active:scale-[0.98]"
                      : "bg-muted text-muted-foreground cursor-not-allowed"
                  )}
                >
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      {t("auth.verifyEmail.submitting")}
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="h-4 w-4" />
                      {t("auth.verifyEmail.submit")}
                    </>
                  )}
                </button>

                {/* Resend section */}
                <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-400">
                  <p className="text-center text-sm text-muted-foreground">
                    {t("auth.verifyEmail.didNotReceive")}
                  </p>

                  <div className="flex justify-center">
                    <button
                      onClick={handleResend}
                      disabled={resendCooldown > 0 || isResending}
                      className={cn(
                        "text-sm font-semibold transition-all duration-200",
                        "flex items-center gap-1.5",
                        resendCooldown > 0 || isResending
                          ? "text-muted-foreground cursor-not-allowed"
                          : "text-accent hover:opacity-80 underline underline-offset-4"
                      )}
                    >
                      <RefreshCw
                        className={cn(
                          "h-3.5 w-3.5",
                          isResending && "animate-spin"
                        )}
                      />
                      {isResending
                        ? t("auth.verifyEmail.resending")
                        : resendCooldown > 0
                          ? t("auth.verifyEmail.resendIn", {
                              seconds: resendCooldown,
                            })
                          : t("auth.verifyEmail.resend")}
                    </button>
                  </div>

                  <p className="text-center text-xs text-muted-foreground/70">
                    {t("auth.verifyEmail.checkSpam")}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Back to login */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-500">
            <div className="border-t border-border pt-4">
              <button
                onClick={() =>
                  navigate(
                    getLocalizedPublicPath(
                      location.pathname,
                      "/login",
                      i18n.resolvedLanguage || i18n.language
                    )
                  )
                }
                className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("auth.verifyEmail.backToLogin")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
